import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Popover } from '@headlessui/react';
import clsx from 'clsx';
import { Button } from './Button';
import { Container } from './Container';
import { useAuth } from '../../../context/DBContext';
import { NavLink } from './NavLink';
function MobileNavLink({ href, children }) {
  return (
    <Popover.Button as={Link} to={href} className="block w-full p-2">
      {children}
    </Popover.Button>
  );
}

function MobileNavIcon({ open }) {
  return (
    <svg
      aria-hidden="true"
      className="h-6 w-6 overflow-visible stroke-slate-700"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <path
        d="M0 1H24M0 12H24M0 23H24"
        className={clsx(
          'origin-center transition',
          open && 'scale-90 opacity-0',
        )}
      />
      <path
        d="M3 3L21 21M21 3L3 21"
        className={clsx(
          'origin-center transition',
          !open && 'scale-90 opacity-0',
        )}
      />
    </svg>
  );
}

function MobileNavigation({ currentUser, handleLogout, getDashboardRoute, ready }) {
  return (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button
            className="relative z-10 flex h-8 w-8 items-center justify-center focus:outline-none"
            aria-label="Toggle Navigation"
          >
            <MobileNavIcon open={open} />
          </Popover.Button>
          <Popover.Overlay
            className={`fixed inset-0 bg-slate-300/50 transition-opacity ${
              open ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <Popover.Panel
            className="absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-2xl bg-white p-4 text-lg text-slate-900 shadow-xl ring-1 ring-slate-900/5"
          >
            <MobileNavLink href="/">Home</MobileNavLink>
            <MobileNavLink href="/about">About</MobileNavLink>
            <MobileNavLink href="/pricing">Pricing</MobileNavLink>
            <MobileNavLink href="/contact">Contact Us</MobileNavLink>
            <hr className="m-2 border-slate-300/40" />
            {!currentUser && (
              <>
                <MobileNavLink href="/login">Sign In</MobileNavLink>
                <MobileNavLink href="/signup">Get Started</MobileNavLink>
              </>
            )}
            {ready && currentUser && (
              <>
                {currentUser.emailVerified ? (
                  <MobileNavLink href={getDashboardRoute()}>
                    My Account
                  </MobileNavLink>
                ) : (
                  <MobileNavLink href="/emailverification">
                    Verify Email
                  </MobileNavLink>
                )}
                <button
                  className="block w-full p-2 text-left "
                  onClick={handleLogout}
                >
                  Sign out
                </button>
              </>
            )}
          </Popover.Panel>
        </>
      )}
    </Popover>
  );
}

export default function HeaderOne({ variant }) {
  const [isSticky, setIsSticky] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const { currentUser, logout, getUserInfo } = useAuth();
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (currentUser) {
        try {
          const data = await getUserInfo(currentUser.uid);
          setUserInfo(data);
        } catch (error) {
          // console.error("Failed to fetch user info:", error);
        }
      }
      setReady(true);
    };
    fetchUserInfo();
  }, [currentUser, getUserInfo]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch {
      // console.error('Failed to log out');
    }
  };

  const getDashboardRoute = () => {
    switch (userInfo?.role) {
      case 'admin':
        return '/admin-user';
      case 'business':
        return '/business-profile';
      case 'local-authority':
        return '/la-users';
      default:
        return '/dashboard';
    }
  };

  return (
    <header
      className={`py-4 ${variant ? variant : ""} ${
        isSticky ? "header__sticky-sticky-menu" : ""
      }`}
    >
      <Container>
        <nav className="relative z-50 flex justify-between items-center">
          <div className="flex items-center md:gap-x-12">
            <Link to="/" aria-label="Home">
              {/* <h3 className="text-xl font-semibold text-slate-900">enableSEND</h3> */}
              <img src="/assets/enablelogo.png" alt="Logo" className="w-[120px]" />

    {/* <img src="/assets/enablelogo.png" alt="Logo" className="w-[120px]" /> */}
            </Link>
            <div className="hidden md:flex md:gap-x-6">
              <NavLink href="/">Home</NavLink>
              <NavLink href="/about">About</NavLink>
              <NavLink href="/pricing">Pricing</NavLink>
              <NavLink href="/contact">Contact Us</NavLink>
            </div>
          </div>
          <div className="flex items-center gap-x-5 md:gap-x-8">
            {!currentUser && (
              <div className="hidden md:block">
                <Button href="/login" color="blue">Sign In</Button>
              </div>
            )}
            {ready && currentUser && (
              <div className="hidden md:block">
                {currentUser.emailVerified ? (
                  <Button href={getDashboardRoute()} color="blue">
                    My Account
                    </Button>
                ) : (
                  <Button href="/emailverification" color="blue">
                    Verify Email
                  </Button>
                )}
                <button
                  className="text-sm text-slate-700 hover:bg-slate-100 hover:text-slate-900 px-4 py-2 rounded-lg"
                  onClick={handleLogout}
                >
                  Sign out
                </button>
              </div>
            )}
            {!currentUser && (
              <div className="hidden md:block">
                <Button href="/signup" color="blue">
                  Get started
                </Button>
              </div>
            )}
            <div className="-mr-1 md:hidden">
              <MobileNavigation
                currentUser={currentUser}
                handleLogout={handleLogout}
                getDashboardRoute={getDashboardRoute}
                ready={ready}
              />
            </div>
          </div>
        </nav>
      </Container>
    </header>
  );
}
